import { Checkbox, Drawer, FormControlLabel, Grid2 } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { StandardButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { StandardTextValidator } from "../../_components/textValidator/standardTextValidator";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { eventService } from "../../_services/event.service";
import { IEventManage } from "../../_common/models/event/eventManage.model";
import { DataTypeEnum } from "../../_common/enum/dataType.enum";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autocomplete";
import { getDateString, getFormatedDate } from "../../_helpers/dateTime.helper";
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
export const EventManageDrawer = (params: { isOpen?: boolean, onSave(): void, onClose(): void }) => {
    const { id } = useParams<{ id: string }>();

    const resetData = (): IEventManage => ({
        id: id ? Number(id) : 0,
        name: '',
        centreId: null,
        description: '',
        startDate: "",
        endDate: "",
        isAllDay: false,
        isActive: true
    });

    const [data, setData] = useState<IEventManage>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const editorRef = useRef<TinyMCEEditor | null>(null);

    useEffect(() => {
        if (id != null)
            fetchData();
        else
            setData(resetData);
    }, [id]);

    useEffect(() => {
        if (isOpen == false)
            setIsOpen(true);
    }, []);

    function fetchData() {
        if (!id) return;

        setIsLoading(true);
        return eventService
            .get(id)
            .then(
                (json: IEventManage) => {
                    setData({
                        ...json,
                        centreId: json.centreId && json.centreId !== 0 ? json.centreId : null,
                    });
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);
        return eventService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    resetData();
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!id) return;
        setIsLoading(true);
        return eventService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    params.onSave();
                }, handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    function handleCheckboxChange(event: any) {
        setData((__data: any) => { return { ...__data, [event.target.name]: event.target.checked } });
    };

    const handleEditorChange = (content: any, editor: any) => {
        setData((_description: any) => {
            return {
                ..._description,
                description: content
            }
        });
    };

    const tinyMceSettings = {
        content_style: "body {font-family:Helvetica;}",
        height: 500,
        menubar: 'file edit view insert format table help',
        statusbar: false,
        placeholder: 'Enter description here...',
        plugins: 'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media template codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion',
        toolbar: "undo redo | bold italic underline strikethrough | align numlist bullist | blocks fontfamily fontsize | link image | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | table media | accordion accordionremove | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl"
    };

    return (
        <Drawer
            anchor={"right"}
            variant="persistent"
            open={isOpen}
            ModalProps={{
                keepMounted: true
            }}>

            <DrawerHeader name={(!!id) ? "Update Event" : "Create Event"} onClose={params.onClose} />

            <ValidatorForm
                onSubmit={submit}
                noValidate>

                <Grid2 container spacing={1} sx={{ "padding": "8px" }}>
                    <Grid2 size={{ xs: 12 }}>
                        <StandardTextValidator
                            key="name"
                            name="name"
                            label="Name"
                            required
                            fullWidth
                            validators={['required', 'minStringLength:1']}
                            errorMessages={['required field', "Field cannot be empty"]}
                            value={data.name}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <DataTypeAutoComplete
                            name="centreId"
                            label="Centre"
                            value={data.centreId ?? null}
                            onChange={handleChange} dataType={DataTypeEnum.Centres} />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            name="startDate"
                            label="Start Date"
                            required
                            fullWidth
                            type="date"
                            value={getFormatedDate(data.startDate)}
                            onChange={handleChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 6 }}>
                        <StandardTextValidator
                            name="endDate"
                            label="End Date"
                            fullWidth
                            type="date"
                            value={getFormatedDate(data.endDate)}
                            onChange={handleChange}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <FormControlLabel checked={!!data.isAllDay} name="isAllDay"
                            control={<Checkbox onChange={handleCheckboxChange} color={"primary"} />}
                            label="All Day Event"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Editor
                            tagName='description'
                            textareaName='description'
                            onInit={(_evt, editor) => editorRef.current = editor}
                            value={data.description}
                            apiKey='5ea9ppzcmew36gv61axb9ztq1tyi8liqm12ht880on2wf3yh'
                            disabled={false}
                            init={tinyMceSettings}
                            onEditorChange={handleEditorChange} />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container spacing={1}>
                            <Grid2 size={{ xs: !data.id ? 12 : data?.isActive === false ? 12 : 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit">
                                    {(data?.isActive === false) ? "Save and Reactivate" : "Save"}
                                </StandardButton>
                            </Grid2>

                            {(!!id && data?.isActive === true) &&
                                <Grid2 size={{ xs: 6 }}>
                                    <StandardButton
                                        saving={saving}
                                        loading={isLoading}
                                        icon="block"
                                        variant="error"
                                        onClick={disable}>
                                        Disable
                                    </StandardButton>
                                </Grid2>
                            }
                        </Grid2>
                    </Grid2>
                </Grid2>

            </ValidatorForm>
        </Drawer>
    );
};